export const isDefaultMedia = (img) =>
  img?.default === true || img?.isDefault === true;

/** Resolve default flag for API payloads; explicit `default` wins over stale `isDefault`. */
export const resolveMediaDefaultFlag = (img, override) => {
  if (override !== undefined) return override === true;
  if (img?.default === true) return true;
  if (img?.default === false) return false;
  if (img?.isDefault === false) return false;
  return img?.isDefault === true;
};

export const hasExplicitDefaultFlag = (img) =>
  img?.default === true ||
  img?.default === false ||
  img?.isDefault === true ||
  img?.isDefault === false;

/** Use image flags when present; otherwise resolve from fallback media. */
export const resolveMediaDefaultFromSourceOrFallback = (img, fallback) =>
  hasExplicitDefaultFlag(img)
    ? resolveMediaDefaultFlag(img)
    : resolveMediaDefaultFlag(fallback);

/**
 * Collapse duplicate `path` entries (e.g. from admin media merges).
 * Keeps first position; merges metadata and default flags.
 */
export const dedupeMediaByPath = (images) => {
  if (!Array.isArray(images)) return [];

  const merged = [];
  const indexByPath = new Map();

  for (const img of images) {
    if (!img?.path) continue;

    const existingIndex = indexByPath.get(img.path);
    if (existingIndex === undefined) {
      const index = merged.length;
      indexByPath.set(img.path, index);
      merged.push({ ...img, order: index });
      continue;
    }

    const existing = merged[existingIndex];
    const isDefault = isDefaultMedia(existing) || isDefaultMedia(img);
    merged[existingIndex] = {
      ...existing,
      ...img,
      default: isDefault,
      isDefault: isDefault,
      size: img.size || existing.size || 0,
      order: existingIndex,
      width: img.width ?? existing.width,
      height: img.height ?? existing.height,
      source: img.source !== undefined ? img.source : existing.source,
    };
  }

  return merged;
};

/** Media `order` is a number (array index). */
export const formatMediaOrder = (order, fallback = 0) => {
  if (typeof order === "number" && Number.isFinite(order)) return order;
  if (
    typeof order === "string" &&
    order !== "" &&
    !Number.isNaN(Number(order))
  ) {
    return Number(order);
  }
  return Number(fallback);
};

/** Assign `order` as 0-based array index on every item. */
export const reindexMediaOrders = (images) =>
  dedupeMediaByPath(images).map((img, index) => ({ ...img, order: index }));

/**
 * Extensions the storefront can render in an <img>. GS1 imports also push
 * archive/video assets (zip, mp4) into `media[]`; those must never be
 * presented as images.
 */
const DISPLAYABLE_IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "avif",
  "bmp",
  "svg",
]);

/**
 * True when a media path can be shown in an <img>. Paths without a
 * recognizable extension are kept (unknown ≠ known-bad, and legacy keys may
 * be extensionless); known non-image extensions (zip, mp4, …) are rejected.
 * @param {unknown} path
 */
export const isDisplayableMediaPath = (path) => {
  if (typeof path !== "string" || !path) return false;
  if (path.startsWith("data:")) return path.startsWith("data:image/");

  const clean = path.split("?")[0];
  const dot = clean.lastIndexOf(".");
  if (dot <= 0 || dot === clean.length - 1) return true;
  const ext = clean.slice(dot + 1).toLowerCase();
  if (ext.includes("/")) return true;
  return DISPLAYABLE_IMAGE_EXTENSIONS.has(ext);
};

/**
 * Media entries that can be rendered as images.
 * @param {Array<Record<string, unknown>> | null | undefined} media
 */
export const getDisplayableMedia = (media) =>
  Array.isArray(media)
    ? media.filter((entry) => isDisplayableMediaPath(entry?.path))
    : [];

/**
 * Index of the image to display: `default: true` first, else first image with
 * a displayable path. Non-image media (zip/mp4) are never selected.
 * @param {Array<Record<string, unknown>> | null | undefined} media
 */
export const getDefaultMediaIndex = (media) => {
  if (!Array.isArray(media) || !media.length) return -1;

  for (let i = 0; i < media.length; i++) {
    if (isDisplayableMediaPath(media[i]?.path) && isDefaultMedia(media[i])) {
      return i;
    }
  }

  for (let i = 0; i < media.length; i++) {
    if (isDisplayableMediaPath(media[i]?.path)) return i;
  }

  return -1;
};

/** @param {Array<Record<string, unknown>> | null | undefined} media */
export const getDefaultMediaItem = (media) => {
  const index = getDefaultMediaIndex(media);
  if (index < 0) return null;
  return media[index];
};

/** @param {Array<Record<string, unknown>> | null | undefined} media */
export const getDefaultMediaPath = (media) =>
  getDefaultMediaItem(media)?.path || null;
